/**
 * Gluon page: Mobile feature management.
 *
 * Lets internal staff toggle the 8 mobile-scoped FeatureType flags for any
 * customer application. Reuses `PUT /gluon/applications/features` (the same
 * endpoint that powers the per-application flags table on the Applications
 * page) so there's no separate backend service to keep in sync.
 *
 * The mobile app fetches its enabled flags via `GET /v1/mobile-app/feature-set`
 * which reads from the same `applications.features[]` array — flips here
 * propagate to the next mobile session refresh.
 *
 * Mirrors the visual style of MobileVersionPolicyPage (MUI Joy + cards).
 */

import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Switch from '@mui/joy/Switch'
import Typography from '@mui/joy/Typography'
import { ClientApplication, FeatureType, IFeature } from '@numeo/types-core'
import { MOBILE_DISPATCHER_FLAGS, MOBILE_DRIVER_FLAGS, MOBILE_FEATURE_LABELS, mobileFeaturesApi } from 'api/mobileFeatures'

/**
 * Effective defaults that the backend's `/v1/mobile-app/feature-set`
 * endpoint applies when a company has no explicit Mobile* flags stored.
 * Mirrored here so the Gluon UI can show the same effective state the
 * mobile app will actually see, instead of falsely showing every toggle
 * as off for un-migrated companies.
 *
 * Keep in sync with `DEFAULT_DISPATCHER_MOBILE_FEATURES` in main-service's
 * feature-set-routes.ts. Drivers do not have an "implicit" default because
 * driver access is invite-only — a driver with no flags configured wouldn't
 * be able to use the app meaningfully without an admin acting first.
 */
const IMPLICIT_DISPATCHER_DEFAULTS: ReadonlySet<FeatureType> = new Set(MOBILE_DISPATCHER_FLAGS)

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

const ALL_MOBILE_FLAGS: ReadonlySet<FeatureType> = new Set([...MOBILE_DISPATCHER_FLAGS, ...MOBILE_DRIVER_FLAGS])

/**
 * Whether the application has any explicit Mobile* flag stored. When false,
 * the backend's fallback path applies — the toggles in the UI should
 * reflect that effective state, not the literal "nothing stored" state.
 */
function hasExplicitMobileFlags(app: ClientApplication): boolean {
    return !!app.features?.some((f) => MOBILE_DISPATCHER_FLAGS.includes(f.feature) || MOBILE_DRIVER_FLAGS.includes(f.feature))
}

/**
 * Read the EFFECTIVE state of a flag for an application. If the app has any
 * explicit Mobile* entries, only those count (absent flag = off — exactly
 * what the backend does once flags are stored). If the app has no explicit
 * mobile flags at all, return the implicit default for dispatcher flags so
 * the UI matches what the mobile app actually sees.
 */
function readFlag(app: ClientApplication, flag: FeatureType): boolean {
    const explicit = app.features?.find((f) => f.feature === flag)
    if (explicit) return !!explicit.enabled
    if (!hasExplicitMobileFlags(app)) {
        return IMPLICIT_DISPATCHER_DEFAULTS.has(flag)
    }
    return false
}

/**
 * Merge a partial mobile-flag update into the application's complete
 * features list. Preserves all non-mobile flags as-is so we never drop a
 * flag just because the page didn't display it.
 */
function mergeFeatures(app: ClientApplication, mobileChanges: Map<FeatureType, boolean>): IFeature[] {
    const existing = app.features ?? []
    const byFlag = new Map<FeatureType, IFeature>(existing.map((f) => [f.feature, f]))
    for (const [flag, enabled] of mobileChanges.entries()) {
        byFlag.set(flag, { feature: flag, enabled })
    }
    // Backfill missing mobile flags with `enabled: false` so the array
    // canonically contains every mobile flag (makes the source-of-truth obvious).
    for (const flag of ALL_MOBILE_FLAGS) {
        if (!byFlag.has(flag)) byFlag.set(flag, { feature: flag, enabled: false })
    }
    return Array.from(byFlag.values())
}

export default function MobileFeaturesPage() {
    const [searchQuery, setSearchQuery] = useState('')
    const [searching, setSearching] = useState(false)
    const [results, setResults] = useState<ClientApplication[]>([])
    const [selected, setSelected] = useState<ClientApplication | null>(null)
    const [draft, setDraft] = useState<Map<FeatureType, boolean>>(new Map())
    const [saving, setSaving] = useState(false)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    // Debounced search.
    useEffect(() => {
        if (!searchQuery.trim()) {
            setResults([])
            return
        }
        const timer = setTimeout(async () => {
            setSearching(true)
            try {
                const apps = await mobileFeaturesApi.searchApplications(searchQuery.trim())
                setResults(apps)
            } catch {
                setResults([])
                setSnackbar({ open: true, message: 'Search failed', color: 'danger' })
            } finally {
                setSearching(false)
            }
        }, 300)
        return () => clearTimeout(timer)
    }, [searchQuery])

    function selectApp(app: ClientApplication) {
        setSelected(app)
        // Initialize the draft from the current state.
        const initial = new Map<FeatureType, boolean>()
        for (const flag of ALL_MOBILE_FLAGS) {
            initial.set(flag, readFlag(app, flag))
        }
        setDraft(initial)
    }

    function toggleFlag(flag: FeatureType) {
        if (!selected) return
        const next = new Map(draft)
        next.set(flag, !next.get(flag))
        setDraft(next)
    }

    const dirty = useMemo(() => {
        if (!selected) return false
        // If the company has no explicit mobile flags yet, treat the page as
        // dirty so admins can persist the implicit defaults with one click.
        // Without this, the Save button would stay disabled (draft matches the
        // computed implicit state) and operators couldn't make the state
        // explicit without first toggling something.
        if (!hasExplicitMobileFlags(selected)) return true
        for (const [flag, enabled] of draft.entries()) {
            if (readFlag(selected, flag) !== enabled) return true
        }
        return false
    }, [draft, selected])

    async function save() {
        if (!selected || !dirty) return
        setSaving(true)
        try {
            const merged = mergeFeatures(selected, draft)
            await mobileFeaturesApi.updateFeatures(selected.projectName, merged)
            // Optimistically update the local copy so subsequent edits compare against the new state.
            const updated: ClientApplication = { ...selected, features: merged }
            setSelected(updated)
            setResults((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
            setSnackbar({ open: true, message: 'Mobile feature flags updated', color: 'success' })
        } catch (err) {
            setSnackbar({ open: true, message: `Save failed: ${(err as Error).message}`, color: 'danger' })
        } finally {
            setSaving(false)
        }
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
            <Typography level="h2" sx={{ mb: 1 }}>
                Mobile Feature Management
            </Typography>
            <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
                Toggle dispatcher / driver tabs for the unified mobile app, per company. Changes propagate to the next mobile session refresh (≤ 24h cache).
            </Typography>

            <Card sx={{ mb: 3 }}>
                <FormControl>
                    <FormLabel>Search company by project name</FormLabel>
                    <Input
                        placeholder="e.g. acme-trucking"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        endDecorator={searching ? <CircularProgress size="sm" /> : undefined}
                    />
                </FormControl>
                {results.length > 0 && (
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        {results.map((app) => (
                            <Box
                                key={app.id}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    bgcolor: selected?.id === app.id ? 'primary.softBg' : 'background.level1',
                                    '&:hover': { bgcolor: 'primary.softBg' },
                                    border: '1px solid',
                                    borderColor: selected?.id === app.id ? 'primary.outlinedBorder' : 'transparent',
                                }}
                                onClick={() => selectApp(app)}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography level="title-sm">{app.projectName}</Typography>
                                        <Typography level="body-xs" color="neutral">
                                            {app.settings?.company?.name ?? '—'}
                                        </Typography>
                                    </Box>
                                    <Chip size="sm" variant="soft" color={app.status === 'active' ? 'success' : 'neutral'}>
                                        {app.status}
                                    </Chip>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Card>

            {selected && (
                <Card>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                        <Box>
                            <Typography level="title-lg">{selected.projectName}</Typography>
                            <Typography level="body-sm" color="neutral">
                                {selected.settings?.company?.name ?? '—'}
                            </Typography>
                        </Box>
                        <Button onClick={save} disabled={!dirty || saving} loading={saving}>
                            Save changes
                        </Button>
                    </Stack>

                    {!hasExplicitMobileFlags(selected) && (
                        <Alert color="primary" sx={{ mb: 2 }}>
                            This company has no Mobile* flags stored yet. Toggles below show the implicit dispatcher defaults the mobile app currently sees.
                            Click Save to persist them as explicit values for this company.
                        </Alert>
                    )}

                    <Typography level="title-sm" sx={{ mt: 1, mb: 1 }}>
                        Dispatcher Mobile
                    </Typography>
                    <Stack spacing={1}>
                        {MOBILE_DISPATCHER_FLAGS.map((flag) => (
                            <FlagToggle key={flag} flag={flag} enabled={!!draft.get(flag)} onToggle={() => toggleFlag(flag)} />
                        ))}
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Typography level="title-sm" sx={{ mb: 1 }}>
                        Driver Mobile
                    </Typography>
                    <Stack spacing={1}>
                        {MOBILE_DRIVER_FLAGS.map((flag) => (
                            <FlagToggle key={flag} flag={flag} enabled={!!draft.get(flag)} onToggle={() => toggleFlag(flag)} />
                        ))}
                    </Stack>

                    {dirty && (
                        <Alert color="warning" sx={{ mt: 2 }}>
                            Unsaved changes
                        </Alert>
                    )}
                </Card>
            )}

            <Snackbar open={snackbar.open} color={snackbar.color} onClose={() => setSnackbar({ ...snackbar, open: false })} autoHideDuration={3000}>
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}

interface FlagToggleProps {
    flag: FeatureType
    enabled: boolean
    onToggle: () => void
}

function FlagToggle({ flag, enabled, onToggle }: FlagToggleProps) {
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
                p: 1.5,
                borderRadius: 8,
                bgcolor: 'background.level1',
            }}
        >
            <Box>
                <Typography level="title-sm">{MOBILE_FEATURE_LABELS[flag] ?? flag}</Typography>
                <Typography level="body-xs" color="neutral">
                    {flag}
                </Typography>
            </Box>
            <Switch checked={enabled} onChange={onToggle} />
        </Stack>
    )
}
